import _isEqual from 'lodash/isEqual'
import { useTranslation } from 'next-i18next'
import { useNextSanityImage } from 'next-sanity-image'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type * as z from 'zod'

import Stack from '@nxt-insurance/ui-kit/components/layoutComponents/Stack'
import { ICON_SIZE } from '@nxt-insurance/ui-kit/constants'
import { body2, h4 } from '@nxt-insurance/ui-kit/core/typography'
import type { Color } from '@nxt-insurance/ui-kit/types'

import { runQuery, sanityClientLike } from '@/cms/client'
import { marketQuery } from '@/cms/market'
import { Select, SelectItem } from '@/components/formComponents/Select'
import { APP_ENV } from '@/config/env'
import { EVENTS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import { reportError, reportErrorMessage } from '@/services/errorReporting'
import { getLocaleParts } from '@/utils/locale'
import { Label } from '@radix-ui/react-label'
import { useQuery } from '@tanstack/react-query'

const MarketSwitcherOption = (props: {
  market: z.infer<typeof marketQuery.schema>[number] & { locale: string }
}) => {
  const imageProps = useNextSanityImage(sanityClientLike, props.market.flagImage.asset, {
    imageBuilder: (imageUrlBuilder) => imageUrlBuilder.width(ICON_SIZE.small),
  })
  return (
    <div className="flex flex-1 flex-row items-center" id={`marketOption_${props.market.market}`}>
      <Image {...imageProps} alt="" />
      <span className={body2({ className: 'pl-2.5' })}>{props.market.name}</span>
    </div>
  )
}

const MarketSwitcherComponent = (props: {
  pageColor?: Color
  backgroundColor?: Color
  width?: number
}) => {
  const { track } = useAnalytics()
  const [width, setWidth] = useState(props.width)
  const { locale, defaultLocale, locales, push } = useRouter()

  const [currentLocale, setCurrentLocale] = useState(
    locale === 'default' || locale === undefined ? defaultLocale : locale
  )
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [lang] = getLocaleParts(currentLocale as string)
  const { t } = useTranslation()
  const { data: marketData } = useQuery({
    queryKey: ['marketData'],
    queryFn: async () => runQuery(marketQuery, { lang }),
    enabled: Boolean(lang),
    // if the query fails then Next.js will show the error page
    throwOnError: true,
  })

  const marketsList = useMemo(() => {
    if (!locale || !defaultLocale || !locales || !marketData) {
      return []
    }
    const markets = marketData.filter(
      (market) =>
        APP_ENV === 'development' ||
        APP_ENV === 'testing' ||
        market.availableEnvironments?.includes(APP_ENV)
    )

    const localesWithoutDefault = locales.filter((l) => l !== 'default')

    return markets
      .map((market) => {
        const localeWithCurrentLang = localesWithoutDefault.find(
          (l) => l === `${lang}-${market.market}`
        )

        if (localeWithCurrentLang) {
          return {
            ...market,
            locale: localeWithCurrentLang,
          }
        }

        const defaultMarketLocale = localesWithoutDefault.find((l) =>
          l.endsWith(`-${market.market}`)
        )
        if (!defaultMarketLocale) {
          // We don't want to crash the app completely, instead we report the error and allow other markets to work properly
          reportErrorMessage(
            `Assertion: No locale for market ${market.market}, languages defined in Sanity: ${market.availableLanguages}`
          )
          return null
        }

        return {
          ...market,
          locale: defaultMarketLocale,
        }
      })
      .filter(
        (market): market is z.infer<typeof marketQuery.schema>[number] & { locale: string } =>
          market !== null
      )
  }, [locale, defaultLocale, locales, marketData, lang])

  useEffect(() => {
    const onResize = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.getBoundingClientRect().width)
      }
    }
    if (typeof window === 'undefined') {
      return
    }
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [])

  // The Select popoper should have the same width as the container
  const widthRef = useCallback((node: HTMLDivElement | null) => {
    if (node !== null) {
      setWidth(node.getBoundingClientRect().width)
      containerRef.current = node
    }
  }, [])

  const label = t('navFooterHeader.region')

  return (
    <div className="w-full max-w-[292px]" ref={widthRef}>
      <Stack space="small">
        <Label className={h4()} htmlFor="marketSwitcher">
          {label}
        </Label>
        <Select
          id="marketSwitcher"
          triggerIcon={
            <Image
              src="/assets/images/svg/chevron-down.svg"
              width="16"
              height="9"
              alt=""
              unoptimized
              loader={({ src }) => src}
            />
          }
          backgroundColor={props.backgroundColor}
          borderColor={props.pageColor}
          name="marketSwitcher"
          defaultValue={currentLocale}
          value={currentLocale}
          width={width}
          label={label}
          onValueChange={async (selected) => {
            track(EVENTS.RegionSwitch, { region: getLocaleParts(selected)[1] })
            push('/', undefined, { locale: `${selected}` }).catch(reportError)
            setCurrentLocale(selected)
          }}
        >
          {marketsList.map((market) => (
            <SelectItem key={market.locale} value={market.locale}>
              <MarketSwitcherOption market={market} />
            </SelectItem>
          ))}
        </Select>
      </Stack>
    </div>
  )
}

// We only want to re-render this if props change since this component does not
// depend on any rich content information
export const MarketSwitcher = memo(MarketSwitcherComponent, _isEqual)
